<template>
    <div>
        <div>
            <div class="header">
                <h2>
                    API Keys
                    <small>You have {{ total }} API Keys</small>
                </h2>
                <div>
                    <a href="javascript:;" title="Click here to create a new API Key" @click="showFormModal()" class="btn">New API Key</a>
                </div>
            </div>

            <table class="table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th class="center">Type</th>
                        <th>Token</th>
                        <th class="center">Last used</th>
                        <th class="center">Created on</th>
                        <th class="center">&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="key in keys" :key="key.token" :class="{'disabled': key.removed }">
                        <td>
                            <span>
                                <template v-if="key.name">{{ key.name }}</template>
                                <template v-else><em>Unnamed key</em></template>
                            </span>
                            <a href="javascript:;" title="Change your key name" @click="showFormModal(key)" class="edit">
                                <svg-edit />
                            </a>
                        </td>
                        <td class="center">
                            <span
                                class="key-type-badge"
                                :class="{ 'master': key.is_master, 'basic': !key.is_master }"
                                :title="key.is_master ? 'Master keys can update account settings, manage API keys, and retrieve invoices.' : 'Basic keys can only perform conversions and check credits.'"
                            >
                                {{ key.is_master ? 'Master' : 'Basic' }}
                            </span>
                        </td>
                        <td class="full token">
                            <span :ref="'token-' + key.token">{{ key.token }}</span>
                            <a href="javascript:;" title="Copy this token" class="copy-tooltip" data-copy-message="Copied!" @click="copyToken(key.token, $event)">
                                <svg-copy />
                            </a>
                        </td>
                        <td class="center full">
                            <template v-if="key.last_used">
                                <time class="tooltip tooltip-top" :data-tooltip="'Used ' + key.usage + ' times in the last 7 days'" :datetime="key.executed|isoformat">{{ key.last_used|relative|capitalize }}</time>
                            </template><template v-else>—</template>
                        </td>
                        <td class="center full">
                            <time :datetime="key.executed|isoformat">{{ key.created|datetime('DD MMM YYYY') }}</time>
                        </td>
                        <td class="center full">
                            <a href="javascript:;" title="Edit this key" @click="showKeyOptions($event, key)" class="settings">
                                <svg-three-dots />
                            </a>
                        </td>
                    </tr>
                    <tr v-if="loading" class="loading">
                        <td colspan="6" class="text-center">
                            <svg-loader />
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="!loading && hasPages">
                    <tr>
                        <td class="pagination" colspan="6">
                            <div>
                                <a :class="{ 'disabled': !hasPreviousPage }" href="javascript:;" title="Previous keys" @click="loadPreviousKeys">Previous</a>
                                -
                                <a :class="{ 'disabled': !hasNextPage }" href="javascript:;" title="Next keys" @click="loadNextKeys">Next</a>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>

            <div class="header">
                <h2>
                    How to do a request
                </h2>
                <div>
                    <a href="https://docs.pdfshift.io" title="Click here to create a new API Key" class="btn btn-light" target="_blank">Documentation</a>
                </div>
            </div>

            <div class="code-sample">
                <ul>
                    <li :class="{'active': language === 'javascript'}"><a href="javascript:;" title="See a code sample in Javascript" @click="language = 'javascript'">Javascript</a></li>
                    <li :class="{'active': language === 'python'}"><a href="javascript:;" title="See a code sample in Python" @click="language = 'python'">Python</a></li>
                    <li :class="{'active': language === 'php'}"><a href="javascript:;" title="See a code sample in PHP" @click="language = 'php'">PHP</a></li>
                    <li :class="{'active': language === 'ruby'}"><a href="javascript:;" title="See a code sample in Ruby" @click="language = 'ruby'">Ruby</a></li>
                    <li :class="{'active': language === 'java'}"><a href="javascript:;" title="See a code sample in Java" @click="language = 'java'">Java</a></li>
                    <li :class="{'active': language === 'csharp'}"><a href="javascript:;" title="See a code sample in C-Sharp" @click="language = 'csharp'">C#</a></li>
                    <li :class="{'active': language === 'go'}"><a href="javascript:;" title="See a code sample in Go" @click="language = 'go'">Go</a></li>
                    <li :class="{'active': language === 'n8n'}"><a href="javascript:;" title="See a code sample in N8n" @click="language = 'n8n'">N8n</a></li>
                    <li :class="{'active': language === 'curl'}"><a href="javascript:;" title="See a code sample in cURL" @click="language = 'curl'">cURL</a></li>
                    <li class="copy">
                        <a href="javascript:;" title="Copy this code" class="copy-tooltip" data-copy-message="Copied!" @click="copyCode($event)">
                            <svg-copy />
                        </a>
                    </li>
                </ul>
                <div class="code-content">
                    <pre class="line-numbers" v-show="language === 'javascript'"><code class="language-javascript" ref="code-javascript">// btoa and fetch are needed
global.btoa = (b) => Buffer.from(b).toString('base64')
const fetch = require('node-fetch')

fetch('https://api.pdfshift.io/v3/convert/pdf', {
    method: 'POST',
    headers: {
        'X-API-Key': '{{ selectedApiKey }}',
        'Content-type': 'application/json'
    },
    body: JSON.stringify({
        source: 'https://en.wikipedia.org/wiki/PDF',
        landscape: false,
        use_print: false
    })
}).then(response => {
    response.body.pipe(fs.createWriteStream('wikipedia.pdf'))
})</code></pre>
                    <pre class="line-numbers" v-show="language === 'python'"><code class="language-python" ref="code-python">import requests

response = requests.post(
    'https://api.pdfshift.io/v3/convert/pdf',
    headers={ 'X-API-Key': '{{ selectedApiKey }}' },
    json={
        "source": "https://en.wikipedia.org/wiki/PDF",
        "landscape": False,
        "use_print": False
    }
)

response.raise_for_status()

with open('wikipedia.pdf', 'wb') as f:
    f.write(response.content)</code></pre>
                    <pre class="line-numbers" v-show="language === 'php'"><code class="language-php" ref="code-php">&lt;?php
$curl = curl_init();

curl_setopt_array($curl, array(
    CURLOPT_URL =&gt; "https://api.pdfshift.io/v3/convert/pdf",
    CURLOPT_RETURNTRANSFER =&gt; true,
    CURLOPT_POST =&gt; true,
    CURLOPT_POSTFIELDS =&gt; json_encode(array(
        "source" =&gt; "https://en.wikipedia.org/wiki/PDF",
        "landscape" =&gt; false,
        "use_print" =&gt; false
    )),
    CURLOPT_HTTPHEADER =&gt; array('X-API-Key:{{ selectedApiKey }}', 'Content-Type:application/json')
));

$response = curl_exec($curl);
file_put_contents('wikipedia.pdf', $response);</code></pre>
                    <pre class="line-numbers" v-show="language === 'ruby'"><code class="language-ruby" ref="code-ruby">require 'uri'
require 'net/https'
require 'json' # for hash to_json conversion

uri = URI("https://api.pdfshift.io/v3/convert/pdf")
data = {"source" => "https://en.wikipedia.org/wiki/PDF"}

Net::HTTP.start(uri.host, uri.port, :use_ssl => true) do |http|
    request = Net::HTTP::Post.new(uri.request_uri)
    request.body = data.to_json
    request["Content-Type"] = "application/json"
    request["X-API-Key"] = "{{ selectedApiKey }}"

    response = http.request(request)

    if response.code == '200'
        # Since Ruby 1.9.1 only:
        File.binwrite("wikipedia.pdf", response.body)
    else
        # Handle other codes here
        puts "#{response.code} #{response.body}"
    end
end</code></pre>
                    <pre class="line-numbers" v-show="language === 'java'"><code class="language-java" ref="code-java">import java.io.File;
import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.nio.file.Files;
import java.nio.file.Paths;
import java.nio.file.StandardCopyOption;
import java.time.Duration;
import org.json.JSONObject;

public class Application {

    public static void main(String... args) throws Exception {
        var jsonObject = new JSONObject();
        jsonObject.put("source", "https://en.wikipedia.org/wiki/PDF");
        var httpRequest = HttpRequest.newBuilder()
                .uri(URI.create("https://api.pdfshift.io/v3/convert/pdf"))
                .timeout(Duration.ofSeconds(20))
                .header("Content-Type", "application/json")
                .header("X-API-Key", "{{ selectedApiKey }}")
                .POST(HttpRequest.BodyPublishers.ofString(jsonObject.toString()))
                .build();

        var httpClient = HttpClient.newBuilder()
                .version(HttpClient.Version.HTTP_1_1)
                .build();

        var response = httpClient.send(httpRequest, HttpResponse.BodyHandlers.ofInputStream());

        var statusCode = response.statusCode();
        if (statusCode == 200 || statusCode == 201) {
            // Save the file locally
            var targetFile = new File("src/main/resources/wikipedia.pdf");
            Files.copy(response.body(), targetFile.toPath(), StandardCopyOption.REPLACE_EXISTING);
        } else {
            // error occurred
        }
    }
}</code></pre>
                    <pre class="line-numbers" v-show="language === 'csharp'"><code class="language-csharp" ref="code-csharp">using System;
using RestSharp;
using Newtonsoft.Json;
using System.IO;
using RestSharp.Authenticators;
using RestSharp.Serialization;
using System.Net.Mail;
using System.Net;
using System.Collections.Generic;
using Newtonsoft.Json.Linq;

namespace PDFShiftExample
{
    class Program
    {
        static void Main(string[] args)
        {
            IRestClient client = new RestClient("https://api.pdfshift.io/v3/convert/pdf");

            IRestRequest request = new RestRequest(Method.POST);
            request.AddHeader("X-API-Key", "{{ selectedApiKey }}");

            var json = new
            {
                source = "https://en.wikipedia.org/wiki/PDF"
            };
            request.AddJsonBody(json);

            IRestResponse response = client.Execute(request);
            if (!response.IsSuccessful)
            {
                // Check why status is not int 2xx.
            }
            else
            {
                File.WriteAllBytes("wikipedia.pdf", response.RawBytes);
            }
        }
    }
}</code></pre>
                    <pre class="line-numbers" v-show="language === 'go'"><code class="language-go" ref="code-go">package main

import (
    "bytes"
    "encoding/json"
    "io/ioutil"
    "log"
    "net/http"
)

func main() {
    message := map[string]interface{}{
        "source":  "https://en.wikipedia.org/wiki/PDF",
    }

    bytesRepresentation, err := json.Marshal(message)
    if err != nil {
        log.Fatalln(err)
    }

    client := http.Client{}
    request, err := http.NewRequest("POST", "https://api.pdfshift.io/v3/convert/pdf", bytes.NewBuffer(bytesRepresentation))
    if err != nil {
        log.Fatalln(err)
    }
    request.Header.Set("X-API-Key", "{{ selectedApiKey }}")

    resp, err := client.Do(request)
    if err != nil {
        log.Fatalln(err)
    }

    if resp.StatusCode >= 200 && resp.StatusCode &lt; 300 {
        body, err := ioutil.ReadAll(resp.Body)
        if err != nil {
            log.Fatalln(err)
        }
        // write the response to file
        ioutil.WriteFile("wikipedia.pdf", body, 0644)
    } else {
        // An error occurred
        var result map[string]interface{}

        json.NewDecoder(resp.Body).Decode(&result)

        log.Println(result)
        log.Println(result["data"])
    }
}</code></pre>
                    <div v-show="language === 'n8n'" style="margin: 20px 0; color: #555">
                        We have a dedicated guide on how to use PDFShift with N8n.<br />
                        <a href="https://help.pdfshift.io/integrate-pdfshift-into-your-n8n-workflow" target="_blank" title="Integrate PDFShift into your N8N workflow">Integrate PDFShift into your N8N workflow</a>
                    </div>
                    <pre class="line-numbers" v-show="language === 'curl'"><code class="language-bash" ref="code-curl">curl \
    -H 'X-API-Key: {{ selectedApiKey }}' \
    -H 'Content-Type: application/json' \
    -d '{"source":"https://en.wikipedia.org/wiki/PDF","landscape": false, "use_print": false}' \
    "https://api.pdfshift.io/v3/convert/pdf" \
    -o wikipedia.pdf

# That's all!</code></pre>
                </div>
            </div>
        </div>
        <div ref="popover" class="popover" v-if="activeKey">
            <ul>
                <li><a href="javascript:;" title="Rename this key" @click="showFormModal(activeKey)">Edit this key</a></li>
                <li>
                    <router-link :to="{ name: 'logs', query: { key: activeKey.token } }">Show logs for this key</router-link>
                </li>
                <li v-if="activeKey.removed" class="enable"><a href="javascript:;" title="Re-enable" @click="toggleState(activeKey, false)">Re-Enable this key</a></li>
                <li v-else class="disable"><a href="javascript:;" title="Disable this key" @click="toggleState(activeKey, true)">Disable this key</a></li>
            </ul>
        </div>
        <modal ref="form" :title="modalTitle" :submit="submitLabel" submitClass="btn" @submit="onSubmit">
            <div :class="{ 'error': errors.name }">
                <label for="form-key-name">Key name</label>
                <input type="text" id="form-key-name" v-model="form.name" name="name" placeholder="Give your key an awesome name." maxlength="250" />
                <p v-if="errors.name">{{ errors.name }}</p>
            </div>

            <div style="margin-bottom: 20px;">
                <label style="display: flex; align-items: center; cursor: pointer;">
                    <input type="checkbox" v-model="form.is_master" style="margin-right: 8px;" />
                    <span style="font-weight: 500;">Master API Key</span>
                </label>
                <p style="margin: 8px 0 0 29px; font-size: 0.9em; color: #878998; font-style: italic;">
                    Master keys can update account settings, manage API keys, and retrieve invoices.<br />
                    Basic keys can only perform conversions and check credits.
                </p>
            </div>
        </modal>
    </div>
</template>

<script>
import SvgLoader from '@/assets/images/loader.svg'
import SvgEdit from '@/assets/images/edit.svg'
import SvgThreeDots from '@/assets/images/threedots.svg'
import SvgCopy from '@/assets/images/copy.svg'
import Modal from '@/components/Modal'

import Prism from 'prismjs'
import 'prismjs/components/prism-markup-templating'
import 'prismjs/plugins/line-numbers/prism-line-numbers.js'
import 'prismjs/plugins/line-numbers/prism-line-numbers.css'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-php'
import 'prismjs/components/prism-ruby'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-csharp'
import 'prismjs/components/prism-go'
import 'prismjs/components/prism-bash'

import '@/assets/css/prism-base16-ateliersulphurpool.light.css'

const LIMIT_PER_PAGE = 10

export default {
    components: { SvgLoader, SvgEdit, SvgThreeDots, SvgCopy, Modal },
    data () {
        return {
            keys: null,
            total: null,
            page: 0,
            loading: true,
            sending: false,
            activeKey: null, // For the menu option on the three dots
            selectedKey: null, // For the form, create or not
            form: {
                name: null,
                is_master: false
            },
            errors: {
                name: null
            },
            language: 'javascript'
        }
    },
    computed: {
        hasPages () {
            return this.total > LIMIT_PER_PAGE
        },
        hasPreviousPage () {
            if (!this.hasPages) return false
            return this.page > 0
        },
        hasNextPage () {
            if (!this.hasPages) return false
            return (this.total > ((this.page + 1) * LIMIT_PER_PAGE))
        },
        selectedApiKey () {
            if (this.keys && this.keys.length > 0) {
                return this.keys.find(x => x.removed === null).token
            }
            return 'your_api_key'
        },
        modalTitle () {
            if (this.selectedKey) {
                return 'Update your API Key'
            }

            return 'Create a new API Key'
        },
        submitLabel () {
            if (this.selectedKey) {
                return 'Update'
            }

            return 'Create'
        }
    },
    watch: {
        language () { this.renderCode() }
    },
    created () {
        this.loadKeys()
    },
    mounted () {
        this.activeKey = null
        window.addEventListener('click', this.closeElements)
    },
    updated () {
        this.renderCode()
    },
    beforeDestroy () {
        window.removeEventListener('click', this.closeElements)
    },
    methods: {
        renderCode () {
            Prism.highlightElement(this.$refs['code-' + this.language])
        },
        loadPreviousKeys () {
            if (this.page === 0) return
            this.page--
            this.loadKeys()
        },
        loadNextKeys () {
            const maxPages = Math.ceil(this.total / LIMIT_PER_PAGE)
            if (this.page === maxPages) return
            this.page++
            this.loadKeys()
        },
        loadKeys () {
            this.invoices = []
            this.loading = true
            this.$http.get('account/api/', { params: { page: this.page } }).then(response => {
                this.keys = response.body.keys.data
                this.total = response.body.keys.total
                this.loading = false
            })
        },
        showKeyOptions ($event, key) {
            $event.stopPropagation()
            if (this.activeKey === key) {
                return this.closeElements()
            }

            this.activeKey = key
            const elemRect = $event.currentTarget.getBoundingClientRect()
            const bodyRect = document.body.getBoundingClientRect()

            this.$nextTick(() => {
                const popoverRect = this.$refs.popover.getBoundingClientRect()

                const top = elemRect.top - bodyRect.top - popoverRect.height - 6
                this.$refs.popover.style.top = `${top}px`

                const left = elemRect.left - popoverRect.width + elemRect.width
                this.$refs.popover.style.left = `${left}px`
                this.$refs.popover.classList.add('visible')
            })
        },
        showFormModal (key) {
            this.form.name = null
            this.form.is_master = false
            this.errors.name = null
            this.selectedKey = null
            if (key) {
                this.selectedKey = key
                this.form.name = key.name
                this.form.is_master = key.is_master
            }

            this.$refs.form.show()
            this.$nextTick(() => {
                this.$refs.form.$el.querySelector('input').focus()
            })
        },
        async onSubmit () {
            if (this.sending) {
                return false
            }
            this.sending = true
            this.errors.name = null

            const payload = {
                name: this.form.name,
                master: this.form.is_master
            }

            let response = null
            try {
                if (this.selectedKey) {
                    response = await this.$http.put('account/api/' + this.selectedKey.token, payload)
                } else {
                    response = await this.$http.post('account/api/', payload)
                }

                this.form.name = null
                this.form.is_master = false
                this.$refs.form.close(true)

                if (this.selectedKey) {
                    // Add new key to list
                    const idx = this.keys.findIndex(x => x.token === this.selectedKey.token)
                    this.keys[idx] = response.body.key
                    this.$toast.success('API Key updated successfully!')
                    this.selectedKey = null
                } else {
                    // Add new key to list
                    this.keys.push(response.body.key)
                    this.total++
                    this.$toast.success('API Key created successfully!')
                }
            } catch (e) {
                this.$set(this.errors, 'name', e.body.error)
            } finally {
                this.sending = false
            }
        },
        toggleState (key, state) {
            const updatingKey = key
            let process = true
            if (state) {
                process = confirm('Please confirm you want to disable your key.')
            }

            if (!process) {
                return false
            }

            const action = state ? 'disable' : 'enable'
            this.$http.post('account/api/' + key.token + '/' + action).then(
                response => {
                    const idx = this.keys.findIndex(x => x.token === updatingKey.token)
                    this.keys[idx] = response.body.key
                },
                response => {
                    this.$toast.error(response.body.error)
                }
            )
            this.closeElements()
        },
        closeElements () {
            if (this.activeKey) {
                this.$refs.popover.classList.remove('visible')
                setTimeout(() => {
                    this.activeKey = null
                }, 300)
            }
        },
        _copyCode (codeTarget, actionTarget) {
            if (document.body.createTextRange) {
                const range = document.body.createTextRange()
                range.moveToElementText(codeTarget)
                range.select()
            } else if (window.getSelection) {
                const selection = window.getSelection()
                const range = document.createRange()
                range.selectNodeContents(codeTarget)
                selection.removeAllRanges()
                selection.addRange(range)
            } else {
                this.$toast.error('An error occured. We could not copy')
                return
            }

            try {
                if (document.execCommand('copy')) {
                    if (window.getSelection) {
                        window.getSelection().removeAllRanges()
                    } else if (document.selection) {
                        document.selection.empty()
                    }

                    actionTarget.classList.add('copied')
                    setTimeout(() => {
                        actionTarget.classList.remove('copied')
                    }, 2500)
                }
            } catch (err) {
                this.$toast.error('An error occured. We could not copy')
            }
        },
        copyCode (evt) {
            this._copyCode(this.$refs['code-' + this.language], evt.currentTarget)
        },
        copyToken (token, evt) {
            this._copyCode(this.$refs['token-' + token][0], evt.currentTarget)
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../public/css/vars';

    .tooltip { border-bottom: dotted 1px #000 }

    .code-sample {
        background: linear-gradient(89.47deg, rgba(108, 32, 205, 0.1) -5.09%, rgba(234, 138, 138, 0.1) 115.63%);
        border-radius: 8px;
        padding: 15px;

        ul { margin: 0 0 10px }

        ul>li {
            display: inline-block;

            &.copy { float: right; }
        }

        li>a {
            display: block;
            margin: 0 10px;
            padding: 5px 20px;
            color: $link-color-secondary;
            border: none;
            background-color: #fff;
            border-radius: 15px;
            font-weight: 500;
            transition: background-color $transition, color $transition, box-shadow $transition;

            &:hover {
                background-color: fade-out($menu-active-color, .25);
                color: #fff;
            }
        }

        li.copy>a {
            background-color: fade-out($menu-active-color, .9);
            svg {
                stroke: #201139;
                height: 16px;
                width: 16px;
            }

            &:hover {
                box-shadow: $box-shadow-secondary;
            }

            &:active {
                box-shadow: inset $box-shadow-secondary;
            }
        }

        li.active>a {
            background-color: $menu-active-color;
            color: #fff;
            box-shadow: $box-shadow-secondary;
        }

        li:first-child>a {
            margin: 0 10px 0 0;
        }

        pre {
            margin: 0;
            padding: 0;
            max-height: 250px;
            overflow-y: auto;
        }

        code {
            border: none;
            background-color: transparent;
            padding: 0;
        }
    }

    table .loading svg {
        height: 24px;
        fill: fade-out($menu-active-color, .2)
    }

    table td.token {
        a {
            border: none;
            display: inline-block;
            margin-left: 5px;

            svg {
                width: 16px;
                height: 16px;
                stroke: $form-text-color;
                vertical-align: middle;
                transition: stroke $transition;
            }

            &:hover>svg {
                stroke: darken($form-text-color, 40%);
            }
        }
    }

    tr.disabled td span { color: fade-out($form-text-color, .5) }
    tr.disabled td.token {
        span {
            color: fade-out($form-text-color, .5);
            text-decoration: line-through;
        }

        a { display: none }
    }

    td a.edit {
        border: none;
        text-decoration: none;
        display: inline-block;
        margin-left: 10px;

        svg {
            stroke: fade-out($form-text-color, .5);
            width: 16px;
            height: 16px;
            transition: stroke $transition, transform $transition;
        }

        &:hover>svg {
            stroke: $form-text-color;
            transform: translate(0px, -2px);
        }
    }

    td:has(a.settings) {
        padding: 0;
    }

    a.settings {
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        border: solid 1px $text-color-light;
        color: $text-color-light;
        border-radius: 4px;
        padding: 8px;
        margin: 0 auto;
        width: fit-content;
        transition: border $transition, color $transition, box-shadow $transition;

        &:hover {
            border: solid 1px $menu-active-color;
            color: $menu-active-color;
            box-shadow: $box-shadow-secondary
        }

        svg {
            transition: fill $transition;
            width: 12px;
            height: 12px;
            fill: currentColor;
            stroke: transparent;
            display: block;
        }
    }

    .popover {
        position: absolute;
        bottom: auto;
        right: auto;
        z-index: 300;
        background-color: #fff;
        padding: 10px 0;
        border-radius: 4px;
        border: solid 1px #ddd;
        box-shadow: $box-shadow-secondary;
        visibility: hidden;
        opacity: 0;
        transition: 0.2s;
        will-change: transform;
        transform: translateY(-5px);

        &.visible {
            visibility: visible;
            opacity: 1;
            transition-delay: 0.1s;
            transform: translateY(0px);
        }

        ul, li {
            margin: 0;
            padding: 0;
        }

        li a {
            display: block;
            padding: 5px 10px;
            text-decoration: none;
            border: none;
            color: $menu-active-color;

            &:hover {
                background-color: fade-out($menu-active-color, .9)
            }
        }

        li.disable a {
            color: $danger-color;
            &:hover {
                background-color: fade-out($danger-color, .9)
            }
        }

        &:after, &:before {
            top: 100%;
            left: auto;
            right: 10px;
            border: solid transparent;
            content: "";
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
        }

        &:after {
            border-color: rgba(255, 255, 255, 0);
            border-top-color: #fff;
            border-width: 5px;
            margin-left: -5px;
        }
        &:before {
            border-color: rgba(221, 221, 221, 0);
            border-top-color: #ddd;
            border-width: 6px;
            margin-left: -6px;
        }
    }

    .pagination {
        background-color: #fff;
        padding: 20px 0;

        &:hover {
            background-color: #fff !important;
        }

        &>div {
            text-align: center;

            a {
                display: inline-block;
                margin: 0 10px;
            }

            a.disabled {
                pointer-events: none;
                cursor: default;
                opacity: 0.7;
                text-decoration: none;
                border-bottom: none;
                color: $text-color-light;
            }
        }
    }

    .key-type-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 0.85em;
        font-weight: 600;
        white-space: nowrap;
        cursor: help;
        transition: transform $transition, box-shadow $transition;

        &.master {
            background-color: #22d488;
            color: #fff;
            box-shadow: 0 2px 4px rgba(34, 212, 136, 0.3);
        }

        &.basic {
            background-color: fade-out($form-text-color, 0.85);
            color: $form-text-color;
        }

        &:hover {
            transform: translateY(-1px);
        }

        &.master:hover {
            box-shadow: 0 4px 8px rgba(34, 212, 136, 0.4);
        }
    }

    tr.disabled .key-type-badge {
        opacity: 0.5;

        &:hover {
            transform: none;
        }
    }
</style>
